import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TelegramSettingInputSchema, type TelegramSettingInput } from '@app/shared';
import { useTelegramSetting, useTestTelegram, useUpdateTelegram } from './telegramApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function TelegramForm(): JSX.Element {
  const { data, isLoading, isError, error } = useTelegramSetting();
  const updateMutation = useUpdateTelegram();
  const testMutation = useTestTelegram();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TelegramSettingInput>({
    resolver: zodResolver(TelegramSettingInputSchema),
    defaultValues: { enabled: false, botToken: '', chatId: '' },
  });

  useEffect(() => {
    if (data) {
      reset({ enabled: data.enabled, botToken: '', chatId: data.chatId });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: TelegramSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset({ ...values, botToken: '' });
      },
    });
  };

  const errMsg = (e: unknown): string => getApiErrorMessage(e, '未知錯誤');

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">Telegram 推播</h2>
      <p className="text-sm text-slate-600">
        全系統單一推播目標。Bot Token 以 AES-256-GCM 加密;Chat ID 可為個人 / 群組 / 頻道。
        取得方式：對 <code>@userinfobot</code> 發訊息可拿到個人 ID;群組需把 Bot 加入後從 getUpdates 取得。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用 Telegram 推播
        </label>

        <Row
          label={`Bot Token${data?.hasBotToken ? '（已設定，留空保留原值）' : ''}`}
          error={errors.botToken?.message}
        >
          <input
            type="password"
            {...register('botToken')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasBotToken ? '（留空保留原 Token）' : '（未設定）'}
          />
        </Row>

        <Row label="Chat ID" error={errors.chatId?.message}>
          <input
            {...register('chatId')}
            className={inputCls}
            placeholder="例：123456789 或 -1001234567890"
          />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">儲存失敗：{errMsg(updateMutation.error)}</div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending || isDirty}
            title={isDirty ? '請先儲存再測試' : '送一則測試訊息至 Chat ID'}
            onClick={() => testMutation.mutate()}
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {testMutation.isPending ? '送出中…' : '測試發送'}
          </button>
        </div>

        {testMutation.isSuccess && testMutation.data ? (
          testMutation.data.ok ? (
            <div role="status" className="text-green-700 text-sm">✓ {testMutation.data.message}</div>
          ) : (
            <div role="alert" className="text-red-600 text-sm">✗ {testMutation.data.message}</div>
          )
        ) : null}
        {testMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">測試失敗：{errMsg(testMutation.error)}</div>
        ) : null}
      </form>
    </section>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? <span role="alert" className="text-red-600 text-xs">{error}</span> : null}
    </label>
  );
}

const inputCls = 'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
